import { firstValueFrom, Observable } from 'rxjs';
import type { StepParams } from '@enlighten/ts-shared/step-api';
import { Secret } from '../../../interfaces/secrets.interface';

/**
 * Shared implementations of EHQ.sendNotification (SCRUM-540) and
 * EHQ.getSecretValue (SCRUM-584) used by the Python step bridge
 * (IntrospectionBridgeService) and the JS step bridge (JavascriptService).
 *
 * Pulled out as pure functions so both runtimes share one source of truth —
 * before extraction the JS bridge maintained an inline copy that drifted from
 * the Python implementation and tripped SonarCloud duplication on SCRUM-673.
 */

export interface AppStateLike {
  projectId?: () => number | undefined;
  project?: () => { projectId?: number } | undefined | null;
}

export interface PushDestinationSendable {
  send(
    projectId: number,
    request: {
      destination: string;
      message: string;
      options?: Record<string, unknown>;
    },
  ): Observable<unknown>;
}

export interface SecretsValueFetchable {
  getSecrets?: (projectId: number) => Observable<Secret[]>;
  getSecretValue?: (
    projectId: number,
    secretId: number,
  ) => Observable<{ value?: string }>;
}

function resolveProjectId(appStateService: AppStateLike): number | undefined {
  return (
    appStateService.projectId?.() ??
    appStateService.project?.()?.projectId ??
    undefined
  );
}

/**
 * Resolves the active project and posts the notification. The shared parser
 * has already validated the arguments (EHQ-10). Returns true on success so
 * step code receives a truthy value through the async import bridge.
 */
export async function sendNotificationViaBridge(
  params: StepParams['EHQ.sendNotification'],
  appStateService: AppStateLike,
  pushDestinationService: PushDestinationSendable | undefined,
): Promise<boolean> {
  const { destination, message, options } = params;

  const projectId = resolveProjectId(appStateService);
  if (!projectId) {
    throw new Error('sendNotification: no active project');
  }
  if (!pushDestinationService?.send) {
    throw new Error('sendNotification: push destination service unavailable');
  }

  await firstValueFrom(
    pushDestinationService.send(projectId, {
      destination,
      message,
      options,
    }),
  );
  return true;
}

/**
 * Resolves nameOrId → secretId, fetches the value, and surfaces a meaningful
 * error when the value is null/undefined (the async bridge would otherwise
 * coerce a void return into true).
 *
 * `lookupByName` is consulted first when the caller passes a string; the
 * Python bridge supplies a sync cache lookup, the JS bridge omits it so the
 * helper falls back to a fresh getSecrets() fetch. Numeric ids skip lookup
 * entirely.
 */
export async function getSecretValueViaBridge(
  params: StepParams['EHQ.getSecretValue'],
  appStateService: AppStateLike,
  secretsService: SecretsValueFetchable | undefined,
  lookupByName?: (name: string) => Secret | undefined,
): Promise<string> {
  const { nameOrId } = params;

  const projectId = resolveProjectId(appStateService);
  if (!projectId) {
    throw new Error('getSecretValue: no active project');
  }
  if (!secretsService?.getSecretValue) {
    throw new Error('getSecretValue: secrets service unavailable');
  }

  const secretId =
    typeof nameOrId === 'number'
      ? nameOrId
      : await resolveSecretIdByName(
          nameOrId,
          projectId,
          secretsService,
          lookupByName,
        );

  const response = await firstValueFrom(
    secretsService.getSecretValue(projectId, secretId),
  );
  if (response?.value === undefined || response?.value === null) {
    throw new Error(
      `getSecretValue: no value returned for secret ${secretId} ` +
      `(it may not be gettable)`,
    );
  }
  return response.value;
}

async function resolveSecretIdByName(
  name: string,
  projectId: number,
  secretsService: SecretsValueFetchable,
  lookupByName: ((name: string) => Secret | undefined) | undefined,
): Promise<number> {
  const cached = lookupByName?.(name);
  if (cached) {
    return cached.secretId;
  }
  // Same wording as enoch's introspection service.
  if (!secretsService.getSecrets) {
    throw new Error(`getSecretValue: secret not found: ${name}`);
  }
  const secrets = await firstValueFrom(secretsService.getSecrets(projectId));
  const match = (secrets ?? []).find(s => s.name === name);
  if (!match) {
    throw new Error(`getSecretValue: secret not found: ${name}`);
  }
  return match.secretId;
}
